import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Activity, Users, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PriceChart from '../trading/PriceChart';
import OrderBook from '../trading/OrderBook';
import TradePanel from '../trading/TradePanel';
import { PostPredictionActivity } from '../dashboard/PostPredictionActivity';
import axios from '../../utils/axiosConfig';
import { fetchMarketBySlug } from '../../services/marketService';

import { usePaperTrading } from '../../contexts/PaperTradingContext';

const TradingPage = () => {
    const { marketId } = useParams();
    const navigate = useNavigate();
    const { balance, positions, totalEquity, resetAccount, refreshPortfolio, loading: contextLoading } = usePaperTrading();
    const [market, setMarket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPostActivity, setShowPostActivity] = useState(false);

    useEffect(() => {
        loadMarket();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketId]);

    const handleResetBalance = async () => {
        try {
            await resetAccount();
            toast.success('Balance reset successfully');
        } catch (e) {
            console.error("Failed to reset balance", e);
            toast.error('Failed to reset balance');
        }
    };

    const loadMarket = async () => {
        setLoading(true);
        try {
            const data = await fetchMarketBySlug(marketId);
            setMarket(data);
        } catch (err) {
            console.error("Failed to load market:", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-[#0b0c0f] text-white flex items-center justify-center">Loading Market Data...</div>;

    return (
        <div className="min-h-screen bg-[#0b0c0f] text-white font-sans selection:bg-emerald-500/30">
            {/* Header */}
            <header className="border-b border-white/5 bg-[#0b0c0f]/95 backdrop-blur sticky top-0 z-50">
                <div className="container mx-auto px-4 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/agent-analysis')} className="text-muted-foreground hover:text-white">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-3">
                            <img src={market?.image || "/assets/logo.png"} className="w-8 h-8 rounded-full bg-white/10" alt="Market" />
                            <div>
                                <h1 className="text-sm font-bold leading-tight">{market?.title || 'Loading...'}</h1>
                                <div className="text-[10px] text-muted-foreground flex gap-2">
                                    <span className="text-emerald-400">Low Risk</span>
                                    <span>•</span>
                                    <span>Vol: {market?.volume?.toString().startsWith('$') ? market.volume : `$${market?.volume || '0'}`}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-4 items-center">
                        {/* Portfolio Stats */}
                        <div className="hidden md:flex flex-col items-end mr-4">
                            <div className="text-xs text-muted-foreground">Total Equity</div>
                            <div className="text-sm font-bold font-mono text-emerald-400">
                                ${totalEquity?.toFixed(2) || '0.00'}
                            </div>
                        </div>
                        <div className="hidden md:flex flex-col items-end mr-4">
                            <div className="text-xs text-muted-foreground">Cash Balance</div>
                            <div className="text-sm font-mono">
                                ${balance?.toFixed(2) || '0.00'}
                            </div>
                        </div>

                        <button
                            onClick={handleResetBalance}
                            className="text-xs flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-muted-foreground hover:text-white"
                            title="Reset Paper Trading Balance"
                        >
                            <RefreshCw className="w-3 h-3" />
                            <span>Reset</span>
                        </button>
                    </div>
                </div>
            </header>

            <div className="container mx-auto p-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Left Column: Trade Panel & Order Book */}
                <div className="lg:col-span-1 space-y-4">
                    <TradePanel
                        market={market}
                        onTradeSuccess={() => {
                            console.log("Prediction Successful - Triggering Activity");
                            setShowPostActivity(true);
                        }}
                    />

                    <div className="h-[400px]">
                        <OrderBook />
                    </div>
                </div>

                {/* Middle Column: Chart & Stats */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="h-[500px]">
                        <PriceChart />
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-4 gap-4">
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold font-mono">{market?.volume?.toString().startsWith('$') ? market.volume : `$${market?.volume || '0'}`}</div>
                            <div className="text-xs text-muted-foreground">Volume</div>
                        </div>
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold font-mono text-emerald-400">
                                {(() => {
                                    const liq = market?.liquidityFormatted || market?.liquidity || '0';
                                    return liq.toString().startsWith('$') ? liq : `$${liq}`;
                                })()}
                            </div>
                            <div className="text-xs text-muted-foreground">Liquidity</div>
                        </div>
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold font-mono text-emerald-400">20</div>
                            <div className="text-xs text-muted-foreground">Yes Holders</div>
                        </div>
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold font-mono text-red-400">20</div>
                            <div className="text-xs text-muted-foreground">No Holders</div>
                        </div>
                    </div>

                    {/* Recent Limit Orders / Activity */}
                    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 min-h-[200px]">
                        <h3 className="text-sm font-bold text-muted-foreground mb-4">Recent Activity</h3>
                        {/* Placeholder for now */}
                        <div className="space-y-2">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="flex justify-between text-xs border-b border-white/5 pb-2">
                                    <div className="flex gap-2">
                                        <span className={i % 2 === 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>
                                            {i % 2 === 0 ? "BOUGHT YES" : "SOLD YES"}
                                        </span>
                                        <span className="text-muted-foreground">via Lobster_Node_{i}</span>
                                    </div>
                                    <div className="text-white font-mono">$1,234.56 • 12s ago</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: AI Agent Activity & Related */}
                <div className="lg:col-span-1 space-y-4">
                    {/* AI Agent Continuous Activity Panel */}
                    <div className="bg-white/[0.02] border border-secondary/20 rounded-xl overflow-hidden">
                        <div className="p-3 border-b border-secondary/10 bg-secondary/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-secondary animate-pulse" />
                                <span className="text-[10px] font-black text-white uppercase tracking-widest">AI Agent Activity</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" />
                                <span className="text-[8px] font-mono text-secondary font-bold uppercase">Live</span>
                            </div>
                        </div>
                        <PostPredictionActivity isVisible={true} />
                    </div>
                    {/* Whale Activity */}
                    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-sm font-bold flex items-center gap-2">
                                <Users className="w-4 h-4 text-secondary" />
                                Whale Activity
                            </h3>
                            <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Live
                            </span>
                        </div>
                        <div className="space-y-3">
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden flex">
                                <div className="w-[30%] bg-emerald-500" />
                                <div className="w-[70%] bg-red-500" />
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>$450k Buys</span>
                                <span>$1.2M Sells</span>
                            </div>
                        </div>
                    </div>

                    {/* Related Markets */}
                    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-sm font-bold">Related Markets</h3>
                        </div>
                        <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="group cursor-pointer hover:bg-white/5 p-2 rounded-lg transition-colors">
                                    <div className="text-xs font-medium line-clamp-2 mb-1 group-hover:text-secondary transition-colors">
                                        Will Bitcoin hit $100k before December?
                                    </div>
                                    <div className="flex justify-between items-center text-[10px]">
                                        <span className="text-emerald-400 font-bold">16% YES</span>
                                        <span className="text-muted-foreground">Vol $2.1M</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default TradingPage;
